
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Droplets, CircleHelp } from 'lucide-react';
import { CycleData, HormoneType } from '@/utils/cycle-tracking';
import { HormoneChartContainer } from './HormoneChartContainer';
import { HormoneTableView } from './HormoneTableView';
import { ChartControls } from './ChartControls';
import { NoDataPlaceholder } from './NoDataPlaceholder';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';

interface HormoneChartCardProps {
  cycleData: CycleData | null;
  selectedHormones: HormoneType[];
  toggleHormone: (hormone: HormoneType) => void;
  showPhases: boolean;
  setShowPhases: (show: boolean) => void;
}

export const HormoneChartCard: React.FC<HormoneChartCardProps> = ({
  cycleData,
  selectedHormones,
  toggleHormone,
  showPhases,
  setShowPhases
}) => {
  const isMobile = useIsMobile();
  
  // Always show phases - we removed the toggle
  React.useEffect(() => {
    setShowPhases(true);
  }, [setShowPhases]);
  
  return (
    <Card className="relative overflow-hidden border-hormony-beige/50">
      <CardHeader className={`${isMobile ? 'py-3 px-4' : 'pb-2'} bg-hormony-cream/30`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Droplets className="h-5 w-5 text-hormony-coral" />
            <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} text-hormony-charcoal`}>Hormone Levels</CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8 border-hormony-beige">
                    <CircleHelp className="h-4 w-4 text-hormony-taupe" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>✨ This chart shows <strong>estimated hormone levels</strong> throughout your cycle based on typical patterns.</p>
                  <p className="mt-2 text-xs">📊 Your actual levels may vary. Hover over the chart for more details!</p>
                  <p className="mt-2 text-xs italic">👆 Scroll horizontally or swipe to see the full cycle data</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Badge variant="outline" className="bg-hormony-cream text-hormony-coral hover:bg-hormony-light-coral/20">
              Beta
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className={isMobile ? 'px-2 pt-0' : ''}>
        <Tabs defaultValue="chart">
          <div className={`flex items-center justify-between ${isMobile ? 'mb-2 flex-col gap-2' : 'mb-4'}`}>
            <TabsList className={isMobile ? 'w-full rounded-full bg-hormony-light-gray' : 'bg-hormony-light-gray'}>
              <TabsTrigger value="chart" className="flex-1 data-[state=active]:bg-hormony-coral data-[state=active]:text-white">Chart View</TabsTrigger>
              <TabsTrigger value="tabular" className="flex-1 data-[state=active]:bg-hormony-coral data-[state=active]:text-white">Tabular View</TabsTrigger>
            </TabsList>
            
            {!isMobile && (
              <ChartControls 
                selectedHormones={selectedHormones}
                toggleHormone={toggleHormone}
                showPhases={true} // Always true now
                setShowPhases={() => {}} // No-op function since we removed the toggle
              />
            )}
          </div>
          
          <TabsContent value="chart" className="relative">
            {cycleData ? (
              <div className="w-full overflow-hidden" style={{ height: isMobile ? '220px' : '350px' }}>
                <HormoneChartContainer 
                  cycleData={cycleData} 
                  selectedHormones={selectedHormones}
                  showPhases={true} // Always true now
                />
              </div>
            ) : (
              <NoDataPlaceholder />
            )}
            
            {isMobile && (
              <div className="mt-2 pt-2 border-t border-hormony-beige/50">
                <ChartControls 
                  selectedHormones={selectedHormones}
                  toggleHormone={toggleHormone}
                  showPhases={true} // Always true now
                  setShowPhases={() => {}} // No-op function
                  isMobile={true}
                />
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="tabular">
            {cycleData ? (
              <div className="overflow-hidden">
                <HormoneTableView 
                  cycleData={cycleData}
                  selectedHormones={selectedHormones}
                />
              </div>
            ) : (
              <NoDataPlaceholder />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
